import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Navbar from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80";

export default function PatientBlogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), page_size: "12" });
      if (q) p.set("q", q);
      if (categoryId) p.set("category_id", categoryId);
      const { data } = await api.get(`/blogs?${p.toString()}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [q, categoryId, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  return (
    <div className="min-h-screen bg-card flex flex-col">
      <Navbar />
      <main data-testid="patient-blogs-page" className="flex-1 px-6 lg:px-10 py-8 max-w-7xl w-full mx-auto space-y-6">
        <header>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Health Articles</h1>
          <p className="mt-1 text-muted-foreground">Evidence-based tips written by our medical editorial team.</p>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="blogs-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search articles" className="pl-9 rounded-full" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setCategoryId(""); setPage(1); }}
            data-testid="blogs-chip-all"
            className={"px-4 py-1.5 rounded-full text-xs font-medium border transition-colors " + (!categoryId ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:bg-secondary")}
          >All</button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { setCategoryId(c.id); setPage(1); }}
              data-testid={`blogs-chip-${c.id}`}
              className={"px-4 py-1.5 rounded-full text-xs font-medium border transition-colors " + (categoryId === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:bg-secondary")}
            >{c.name}</button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3].map((i) => <div key={i} className="h-72 bg-muted rounded-2xl animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-10 text-center text-muted-foreground">No articles match your filters.</div>
        ) : (
          <div data-testid="blogs-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((b) => (
              <Link key={b.id} to={`/patient/blogs/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group bg-white border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <img
                  src={b.cover_image_url || FALLBACK}
                  alt={b.title}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK; }}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <h3 className="font-semibold tracking-tight line-clamp-2 group-hover:text-primary transition-colors">{b.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{b.excerpt}</p>
                  <div className="mt-3 text-xs font-medium text-primary">Read more →</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {total > 12 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 12)}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 12)} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
